import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface CompanyInfo {
  companyName?: string;
  taxId?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  billingEmail?: string;
  billingPhone?: string;
}

interface PaymentMethodsInfo {
  bankName?: string | null;
  bankAccountName?: string | null;
  bankAccountNumber?: string | null;
  bankRoutingNumber?: string | null;
  bankSwiftCode?: string | null;
  zelleName?: string | null;
  zelleEmail?: string | null;
  zellePhone?: string | null;
  venmoHandle?: string | null;
  paypalEmail?: string | null;
}

interface InvoiceData {
  invoiceNumber: string;
  status: string;
  periodStart: string;
  periodEnd: string;
  ownerName: string;
  companySnapshot?: CompanyInfo;
  adminCompanySnapshot?: CompanyInfo;
  orgLogoUrl?: string | null;
  lineItems: Array<{ description: string; amount: number | string; date?: string }>;
  subtotal: number | string;
  cashCollected: number | string;
  settlements: number | string;
  netOwed: number | string;
  notes?: string;
  createdAt?: string;
  issuedAt?: string;
  payLinkUrl?: string | null;
}

interface ContractorInvoiceData {
  invoiceNumber: string;
  status: string;
  weekStartDate?: string;
  weekEndDate?: string;
  totalAmount: number | string;
  notes?: string;
  createdAt?: string;
  contractor?: { fullName?: string; username?: string; role?: string } | null;
  items?: Array<{ description: string; amount: number | string; jobType?: string; jobDate?: string | Date | null }>;
  contractorName?: string;
  orgName?: string;
}

const COLORS = {
  primary: [30, 41, 59] as [number, number, number],
  dark: [15, 23, 42] as [number, number, number],
  text: [51, 65, 85] as [number, number, number],
  gray: [100, 116, 139] as [number, number, number],
  lightGray: [248, 250, 252] as [number, number, number],
  border: [226, 232, 240] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  green: [22, 163, 74] as [number, number, number],
  amber: [217, 119, 6] as [number, number, number],
};

const PAGE = {
  margin: 18,
  topMargin: 22,
  bottomMargin: 22,
  footerHeight: 14,
};

function fmt(amount: string | number | null | undefined): string {
  return `$${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function parseLocalDate(d: string | Date): Date {
  if (d instanceof Date) return d;
  // "YYYY-MM-DD" strings must be parsed as local dates, not UTC midnight.
  // new Date("2026-03-11") treats it as UTC, which shifts the day back in UTC- timezones.
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(d);
}

function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "N/A";
  const date = parseLocalDate(d);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function fmtDateShort(d: string | Date | null | undefined): string {
  if (!d) return "-";
  const date = parseLocalDate(d);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Naive-midnight bounds (period/week) render UTC, else they shift a day earlier in a UTC- browser. Instants stay on fmtDate.
const fmtDateUTC = (d: string | Date | null | undefined): string => !d || isNaN(new Date(d).getTime()) ? "N/A" : new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });

function buildAddressLines(cs: CompanyInfo): string[] {
  const lines: string[] = [];
  if (cs.companyName) lines.push(cs.companyName);
  if (cs.taxId) lines.push(`Tax ID: ${cs.taxId}`);
  if (cs.addressLine1) lines.push(cs.addressLine1);
  if (cs.addressLine2) lines.push(cs.addressLine2);
  const cityLine = [cs.city, cs.state, cs.postalCode].filter(Boolean).join(", ");
  if (cityLine) lines.push(cityLine);
  if (cs.country) lines.push(cs.country);
  if (cs.billingEmail) lines.push(cs.billingEmail);
  if (cs.billingPhone) lines.push(cs.billingPhone);
  return lines;
}

function getUsableHeight(doc: jsPDF): number {
  return doc.internal.pageSize.getHeight() - PAGE.bottomMargin - PAGE.footerHeight;
}

function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > getUsableHeight(doc)) {
    doc.addPage();
    drawPageDecor(doc);
    return PAGE.topMargin + 8;
  }
  return y;
}

function drawPageDecor(doc: jsPDF) {
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 2.5, "F");
}

function drawFooter(doc: jsPDF, text: string) {
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerY = pageHeight - PAGE.footerHeight;

    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.line(PAGE.margin, footerY + 2, pageWidth - PAGE.margin, footerY + 2);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...COLORS.gray);
    doc.text(text, pageWidth / 2, footerY + 8, { align: "center" });

    if (totalPages > 1) {
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - PAGE.margin, footerY + 8, { align: "right" });
    }
  }
}

function drawInfoBox(
  doc: jsPDF,
  label: string,
  lines: string[],
  x: number,
  y: number,
  width: number
): number {
  const lineHeight = 4.8;
  const padding = 8;
  const labelHeight = 5;
  const maxTextWidth = width - padding * 2;

  const wrappedLines: { text: string; bold: boolean }[] = [];
  for (let i = 0; i < lines.length; i++) {
    const splitLines = doc.splitTextToSize(lines[i], maxTextWidth);
    for (const sl of splitLines) {
      wrappedLines.push({ text: sl, bold: i === 0 });
    }
  }

  const boxHeight = padding * 2 + labelHeight + wrappedLines.length * lineHeight;

  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(x, y, width, boxHeight, 2, 2, "F");
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, width, boxHeight, 2, 2, "S");

  let ty = y + padding + 3;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.gray);
  doc.text(label.toUpperCase(), x + padding, ty);
  ty += labelHeight + 1;

  doc.setFontSize(9);
  doc.setTextColor(...COLORS.dark);
  for (const wl of wrappedLines) {
    doc.setFont("helvetica", wl.bold ? "bold" : "normal");
    doc.text(wl.text, x + padding, ty);
    ty += lineHeight;
  }

  return y + boxHeight;
}

function drawSummaryLine(
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  valueX: number,
  opts?: { bold?: boolean; color?: [number, number, number]; large?: boolean }
): number {
  const fontSize = opts?.large ? 11 : 9.5;
  doc.setFont("helvetica", opts?.bold ? "bold" : "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(...(opts?.color || COLORS.text));
  doc.text(label, x, y);
  doc.setTextColor(...(opts?.color || COLORS.dark));
  doc.setFont("helvetica", opts?.bold ? "bold" : "normal");
  doc.text(value, valueX, y, { align: "right" });
  return y + (opts?.large ? 7 : 5.5);
}

function buildInvoiceDoc(invoice: InvoiceData, paymentMethods?: PaymentMethodsInfo | null, logoDataUrl?: string | null): jsPDF {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = PAGE.margin;
  const contentWidth = pageWidth - margin * 2;

  drawPageDecor(doc);

  let y = 16;

  const headerName = invoice.adminCompanySnapshot?.companyName || "Staio";
  const drawTextHeader = () => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(...COLORS.primary);
    doc.text(headerName, margin, y);
    doc.setFontSize(24);
    doc.setTextColor(...COLORS.dark);
    doc.text("INVOICE", pageWidth - margin, y, { align: "right" });
  };

  if (logoDataUrl) {
    try {
      // Scale the logo to fit a 72x22mm box while preserving its aspect ratio.
      const props = doc.getImageProperties(logoDataUrl);
      const scale = Math.min(72 / props.width, 22 / props.height);
      const lw = props.width * scale, lh = props.height * scale;
      doc.addImage(logoDataUrl, margin, y - 2, lw, lh);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      doc.setTextColor(...COLORS.dark);
      doc.text("INVOICE", pageWidth - margin, y + lh / 2 - 2, { align: "right" });
      y += lh + 6;
    } catch {
      drawTextHeader();
      y += 10;
    }
  } else {
    drawTextHeader();
    y += 10;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);

  const metaX = pageWidth - margin;
  doc.text(`Invoice #: ${invoice.invoiceNumber}`, metaX, y, { align: "right" });
  y += 4.5;

  const dateLabel = invoice.issuedAt
    ? `Issued: ${fmtDate(invoice.issuedAt)}`
    : invoice.createdAt
      ? `Created: ${fmtDate(invoice.createdAt)}`
      : "";
  if (dateLabel) {
    doc.text(dateLabel, metaX, y, { align: "right" });
    y += 4.5;
  }

  const statusText = invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1);
  doc.text(`Status: ${statusText}`, metaX, y, { align: "right" });
  y += 4.5;

  doc.text(
    `Period: ${fmtDateUTC(invoice.periodStart)} – ${fmtDateUTC(invoice.periodEnd)}`,
    metaX, y, { align: "right" }
  );

  y += 10;

  const hasFrom = invoice.adminCompanySnapshot?.companyName;
  const hasBillTo = invoice.companySnapshot?.companyName;

  if (hasFrom && hasBillTo) {
    const halfWidth = (contentWidth - 6) / 2;
    const fromLines = buildAddressLines(invoice.adminCompanySnapshot!);
    const billToLines = buildAddressLines(invoice.companySnapshot!);
    const fromBottom = drawInfoBox(doc, "From", fromLines, margin, y, halfWidth);
    const billToBottom = drawInfoBox(doc, "Bill To", billToLines, margin + halfWidth + 6, y, halfWidth);
    y = Math.max(fromBottom, billToBottom) + 10;
  } else if (hasFrom) {
    const fromLines = buildAddressLines(invoice.adminCompanySnapshot!);
    const bottom = drawInfoBox(doc, "From", fromLines, margin, y, contentWidth);
    y = bottom + 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...COLORS.dark);
    doc.text(`Property Manager: ${invoice.ownerName}`, margin, y);
    y += 8;
  } else if (hasBillTo) {
    const billToLines = buildAddressLines(invoice.companySnapshot!);
    const bottom = drawInfoBox(doc, "Bill To", billToLines, margin, y, contentWidth);
    y = bottom + 10;
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...COLORS.dark);
    doc.text(`Property Manager: ${invoice.ownerName}`, margin, y);
    y += 8;
  }

  y = ensureSpace(doc, y, 20);

  const hasAnyDate = (invoice.lineItems || []).some((item) => item.date);
  const tableData = (invoice.lineItems || []).map((item) =>
    hasAnyDate
      ? [item.description, fmtDateShort(item.date), fmt(item.amount)]
      : [item.description, fmt(item.amount)]
  );

  autoTable(doc, {
    startY: y,
    head: [hasAnyDate ? ["Description", "Date", "Amount"] : ["Description", "Amount"]],
    body: tableData,
    foot: [hasAnyDate ? ["Total Service Charges", "", fmt(invoice.subtotal)] : ["Total Service Charges", fmt(invoice.subtotal)]],
    theme: "plain",
    margin: { left: margin, right: margin, bottom: PAGE.footerHeight + PAGE.bottomMargin },
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: "bold",
      fontSize: 9,
      cellPadding: { top: 3, bottom: 3, left: 5, right: 5 },
    },
    bodyStyles: {
      fontSize: 9,
      textColor: COLORS.text,
      cellPadding: { top: 2.5, bottom: 2.5, left: 5, right: 5 },
      lineColor: COLORS.border,
      lineWidth: 0.2,
    },
    footStyles: {
      fillColor: COLORS.lightGray,
      textColor: COLORS.dark,
      fontStyle: "bold",
      fontSize: 9.5,
      cellPadding: { top: 3, bottom: 3, left: 5, right: 5 },
    },
    columnStyles: hasAnyDate ? {
      0: { cellWidth: contentWidth * 0.50 },
      1: { cellWidth: contentWidth * 0.22 },
      2: { cellWidth: contentWidth * 0.28, halign: "right" },
    } : {
      0: { cellWidth: contentWidth * 0.65 },
      1: { cellWidth: contentWidth * 0.35, halign: "right" },
    },
    alternateRowStyles: {
      fillColor: [250, 250, 252],
    },
    didDrawPage: () => {
      drawPageDecor(doc);
    },
  });

  y = (doc as any).lastAutoTable.finalY + 14;

  y = ensureSpace(doc, y, 40);

  const summaryBoxWidth = 90;
  const summaryX = pageWidth - margin - summaryBoxWidth;
  const summaryValueX = pageWidth - margin - 4;
  const summaryBoxBottom = y - 4 + 48; // track box bottom to avoid overlap on left side

  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(summaryX - 4, y - 4, summaryBoxWidth + 4, 48, 2, 2, "F");
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(summaryX - 4, y - 4, summaryBoxWidth + 4, 48, 2, 2, "S");

  y = drawSummaryLine(doc, "Cash Collected", fmt(invoice.cashCollected), summaryX, y, summaryValueX);
  y = drawSummaryLine(doc, "Service Charges", `-${fmt(invoice.subtotal)}`, summaryX, y, summaryValueX);
  y = drawSummaryLine(doc, "Settlements", `-${fmt(invoice.settlements)}`, summaryX, y, summaryValueX);

  y += 2;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.6);
  doc.line(summaryX, y, summaryValueX, y);
  y += 5;

  const netNum = Number(invoice.netOwed || 0);
  const netLabel = netNum >= 0 ? "Net Owed by PM" : "Net Owed to PM";
  const netColor = netNum >= 0 ? COLORS.primary : COLORS.green;
  y = drawSummaryLine(doc, netLabel, fmt(Math.abs(netNum)), summaryX, y, summaryValueX, {
    bold: true,
    color: netColor,
    large: true,
  });

  // Ensure left-side content starts below the summary box
  y = Math.max(y, summaryBoxBottom + 6);

  if (invoice.notes) {
    y += 10;
    y = ensureSpace(doc, y, 20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.gray);
    doc.text("NOTES", margin, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.text);
    const noteLines = doc.splitTextToSize(invoice.notes, contentWidth);
    for (const line of noteLines) {
      y = ensureSpace(doc, y, 5);
      doc.text(line, margin, y);
      y += 4.5;
    }
  }

  const hasVal = (v?: string | null) => !!(v && v.trim());
  const hasPaymentMethods = paymentMethods && (
    hasVal(paymentMethods.bankName) || hasVal(paymentMethods.zelleName) || hasVal(paymentMethods.zelleEmail) ||
    hasVal(paymentMethods.zellePhone) || hasVal(paymentMethods.venmoHandle) || hasVal(paymentMethods.paypalEmail)
  );

  if (hasPaymentMethods) {
    y += 10;
    y = ensureSpace(doc, y, 30);

    const pmLines: string[] = [];
    if (paymentMethods!.bankName) {
      let bankLine = `Bank: ${paymentMethods!.bankName}`;
      if (paymentMethods!.bankAccountName) bankLine += ` – ${paymentMethods!.bankAccountName}`;
      pmLines.push(bankLine);
      if (paymentMethods!.bankAccountNumber) {
        let detailLine = `  Account: ${paymentMethods!.bankAccountNumber}`;
        if (paymentMethods!.bankRoutingNumber) detailLine += `  |  Routing: ${paymentMethods!.bankRoutingNumber}`;
        if (paymentMethods!.bankSwiftCode) detailLine += `  |  SWIFT: ${paymentMethods!.bankSwiftCode}`;
        pmLines.push(detailLine);
      }
    }
    if (paymentMethods!.zelleName || paymentMethods!.zelleEmail || paymentMethods!.zellePhone) {
      let zelleLine = "Zelle: ";
      if (paymentMethods!.zelleName) zelleLine += paymentMethods!.zelleName;
      if (paymentMethods!.zelleEmail) zelleLine += ` (${paymentMethods!.zelleEmail})`;
      else if (paymentMethods!.zellePhone) zelleLine += ` (${paymentMethods!.zellePhone})`;
      pmLines.push(zelleLine);
    }
    if (paymentMethods!.venmoHandle) pmLines.push(`Venmo: ${paymentMethods!.venmoHandle}`);
    if (paymentMethods!.paypalEmail) pmLines.push(`PayPal: ${paymentMethods!.paypalEmail}`);

    const pmBoxHeight = 14 + pmLines.length * 5;

    doc.setFillColor(...COLORS.lightGray);
    doc.roundedRect(margin, y, contentWidth, pmBoxHeight, 2, 2, "F");
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, y, contentWidth, pmBoxHeight, 2, 2, "S");

    let pmY = y + 7;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.gray);
    doc.text("PAYMENT METHODS", margin + 8, pmY);
    pmY += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.dark);
    for (const line of pmLines) {
      const truncated = doc.splitTextToSize(line, contentWidth - 16)[0];
      doc.text(truncated, margin + 8, pmY);
      pmY += 5;
    }
    y += pmBoxHeight; // advance y past the payment methods box
  }

  if (invoice.payLinkUrl) {
    y = ensureSpace(doc, y, 18);
    y += 8;
    const btnW = 72;
    const btnH = 10;
    doc.setFillColor(22, 163, 74);
    doc.roundedRect(margin, y - 7, btnW, btnH, 2.5, 2.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(255, 255, 255);
    doc.textWithLink("Pay online", margin + btnW / 2, y, { url: invoice.payLinkUrl, align: "center" });
    doc.setTextColor(...COLORS.dark);
    y += 6;
  }

  const footerText = invoice.adminCompanySnapshot?.companyName
    ? `Generated by ${invoice.adminCompanySnapshot.companyName} via Staio`
    : "Generated by Staio Property Management";
  drawFooter(doc, footerText);

  return doc;
}

async function fetchLogoDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function generateInvoicePdf(invoice: InvoiceData, paymentMethods?: PaymentMethodsInfo | null) {
  const logoDataUrl = invoice.orgLogoUrl ? await fetchLogoDataUrl(invoice.orgLogoUrl) : null;
  const doc = buildInvoiceDoc(invoice, paymentMethods, logoDataUrl);
  doc.save(`invoice-${invoice.invoiceNumber}.pdf`);
}

export async function getInvoicePdfBase64(invoice: InvoiceData, paymentMethods?: PaymentMethodsInfo | null): Promise<string> {
  const logoDataUrl = invoice.orgLogoUrl ? await fetchLogoDataUrl(invoice.orgLogoUrl) : null;
  return buildInvoiceDoc(invoice, paymentMethods, logoDataUrl).output('datauristring');
}
export function generateContractorInvoicePdf(invoice: ContractorInvoiceData, orgName?: string) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = PAGE.margin;
  const contentWidth = pageWidth - margin * 2;

  drawPageDecor(doc);

  let y = 16;

  const companyName = orgName || invoice.orgName || "Staio";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...COLORS.primary);
  doc.text(companyName, margin, y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...COLORS.dark);
  doc.text("CONTRACTOR INVOICE", pageWidth - margin, y, { align: "right" });

  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  const metaX = pageWidth - margin;

  doc.text(`Invoice #: ${invoice.invoiceNumber}`, metaX, y, { align: "right" });
  y += 4.5;

  if (invoice.createdAt) {
    doc.text(`Created: ${fmtDate(invoice.createdAt)}`, metaX, y, { align: "right" });
    y += 4.5;
  }

  const statusText = invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1);
  const statusColors: Record<string, [number, number, number]> = {
    draft: COLORS.gray,
    pending: COLORS.amber,
    approved: COLORS.green,
    paid: COLORS.green,
  };
  doc.setTextColor(...(statusColors[invoice.status] || COLORS.gray));
  doc.setFont("helvetica", "bold");
  doc.text(`Status: ${statusText}`, metaX, y, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COLORS.gray);
  y += 4.5;

  if (invoice.weekStartDate && invoice.weekEndDate) {
    doc.text(
      `Period: ${fmtDateUTC(invoice.weekStartDate)} – ${fmtDateUTC(invoice.weekEndDate)}`,
      metaX, y, { align: "right" }
    );
  }

  y += 10;

  const contractorName = invoice.contractor?.fullName || invoice.contractor?.username || invoice.contractorName || "Contractor";
  const contractorRole = invoice.contractor?.role === "cleaner" ? "Cleaning Team" : invoice.contractor?.role === "maintenance" ? "Maintenance Crew" : "";

  const contractorLines = [contractorName];
  if (contractorRole) contractorLines.push(contractorRole);

  const bottom = drawInfoBox(doc, "Pay To", contractorLines, margin, y, contentWidth * 0.5);
  y = bottom + 10;

  y = ensureSpace(doc, y, 20);

  const items = invoice.items || [];
  const hasJobDates = items.some(item => item.jobDate);

  const tableData = items.map((item) => {
    const base = [
      item.description,
      item.jobType ? item.jobType.charAt(0).toUpperCase() + item.jobType.slice(1) : "-",
    ];
    if (hasJobDates) base.push(fmtDateShort(item.jobDate));
    base.push(fmt(item.amount));
    return base;
  });

  const headers = hasJobDates ? ["Description", "Type", "Date", "Amount"] : ["Description", "Type", "Amount"];
  const footRow = hasJobDates ? ["", "", "Total", fmt(invoice.totalAmount)] : ["", "Total", fmt(invoice.totalAmount)];

  const descWidth = hasJobDates ? contentWidth * 0.42 : contentWidth * 0.52;
  const typeWidth = contentWidth * 0.18;
  const dateWidth = contentWidth * 0.18;
  const amtWidth = contentWidth * 0.22;

  const colStyles: Record<number, object> = hasJobDates
    ? { 0: { cellWidth: descWidth }, 1: { cellWidth: typeWidth }, 2: { cellWidth: dateWidth }, 3: { cellWidth: amtWidth, halign: "right" } }
    : { 0: { cellWidth: descWidth }, 1: { cellWidth: typeWidth }, 2: { cellWidth: amtWidth, halign: "right" } };

  autoTable(doc, {
    startY: y,
    head: [headers],
    body: tableData,
    foot: [footRow],
    theme: "plain",
    margin: { left: margin, right: margin, bottom: PAGE.footerHeight + PAGE.bottomMargin },
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: "bold",
      fontSize: 9,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
    },
    bodyStyles: {
      fontSize: 9,
      textColor: COLORS.text,
      cellPadding: { top: 2.5, bottom: 2.5, left: 4, right: 4 },
      lineColor: COLORS.border,
      lineWidth: 0.2,
    },
    footStyles: {
      fillColor: COLORS.lightGray,
      textColor: COLORS.dark,
      fontStyle: "bold",
      fontSize: 10,
      cellPadding: { top: 3.5, bottom: 3.5, left: 4, right: 4 },
    },
    columnStyles: colStyles,
    alternateRowStyles: {
      fillColor: [250, 250, 252],
    },
    didDrawPage: () => {
      drawPageDecor(doc);
    },
  });

  y = (doc as any).lastAutoTable.finalY + 14;

  y = ensureSpace(doc, y, 30);

  const totalBoxWidth = 80;
  const totalBoxX = pageWidth - margin - totalBoxWidth;

  doc.setFillColor(...COLORS.primary);
  doc.roundedRect(totalBoxX, y, totalBoxWidth, 22, 3, 3, "F");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.white);
  doc.text("TOTAL AMOUNT", totalBoxX + totalBoxWidth / 2, y + 8, { align: "center" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(fmt(invoice.totalAmount), totalBoxX + totalBoxWidth / 2, y + 17, { align: "center" });

  y += 30;

  if (invoice.notes) {
    y = ensureSpace(doc, y, 20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.gray);
    doc.text("NOTES", margin, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.text);
    const noteLines = doc.splitTextToSize(invoice.notes, contentWidth);
    for (const line of noteLines) {
      y = ensureSpace(doc, y, 5);
      doc.text(line, margin, y);
      y += 4.5;
    }
  }

  const footerText = `Generated by ${companyName} via Staio`;
  drawFooter(doc, footerText);

  doc.save(`contractor-invoice-${invoice.invoiceNumber}.pdf`);
}
